import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import { ApartmentOutlined, KeyboardArrowDown } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApiClient } from '../api/axios';
import { applySelectedSession, finalizeOrganizationAccess, selectOrganization } from '../utils/authToken';
import { readAuthEmail } from '../utils/authSession';
import { showSnackbar } from '../api/snackbarSlice';
import OrganizationSelectModal from '../resources/login/components/OrganizationSelectModal';
import OrganizationMfaModal from '../resources/login/components/OrganizationMfaModal';
import { orgIconBoxSx } from '../resources/login/components/orgIconBoxSx';
import { HOME_ROUTE } from '../constants/defaults';

const getOrgTrn = (org) => org?.settings?.trn || org?.settings?.tax_trn || org?.trn || null;

const OrgSwitcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const org = useSelector((state) => state.org.org);
  const user = useSelector((state) => state.user.user);

  const [open, setOpen] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [organizationsError, setOrganizationsError] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [hasMultipleOrganizations, setHasMultipleOrganizations] = useState(false);
  const [mfaState, setMfaState] = useState(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  const enrichedOrganizations = useMemo(
    () =>
      organizations.map((organization) =>
        organization.id === org?.id ? { ...organization, trn: getOrgTrn(org) ?? organization.trn } : organization,
      ),
    [organizations, org],
  );

  useEffect(() => {
    let cancelled = false;

    const loadOrganizationCount = async () => {
      try {
        const response = await authApiClient.get('/organizations');
        const items = response.data?.organizations ?? [];

        if (!cancelled) {
          setHasMultipleOrganizations(items.length > 1);
        }
      } catch {
        if (!cancelled) {
          setHasMultipleOrganizations(false);
        }
      }
    };

    loadOrganizationCount();

    return () => {
      cancelled = true;
    };
  }, [org?.id]);

  const loadOrganizations = async () => {
    setOrganizationsLoading(true);
    setOrganizationsError(null);

    try {
      const response = await authApiClient.get('/organizations');
      const items = response.data?.organizations ?? [];
      setOrganizations(items);
      setHasMultipleOrganizations(items.length > 1);

      return items;
    } catch (err) {
      const message =
        err?.response?.data?.errors?.message ||
        err?.response?.data?.message ||
        'Unable to load organizations. Please try again.';

      setOrganizationsError(message);

      return [];
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const handleOpen = useCallback(async () => {
    setOpen(true);
    await loadOrganizations();
  }, []);

  const handleClose = () => {
    if (switching || mfaSubmitting) {
      return;
    }

    setOpen(false);
    setOrganizationsError(null);
    setMfaState(null);
  };

  const handleMfaCancel = () => {
    setMfaState(null);
  };

  const handleSelectOrganization = async (organizationId) => {
    if (!organizationId || organizationId === org?.id) {
      handleClose();

      return;
    }

    setSwitching(true);
    setOrganizationsError(null);

    const selectedOrg = organizations.find((organization) => organization.id === organizationId);

    try {
      const selectResponse = await selectOrganization(authApiClient, organizationId);

      const result = await finalizeOrganizationAccess({
        selectResponse,
        organizationId,
        dispatch,
        queryClient,
        onMfaRequired: (payload) => {
          if (payload.message) {
            dispatch(
              showSnackbar({
                message: payload.message,
                severity: 'success',
              }),
            );
          }

          setOpen(false);
          setMfaState({
            organizationId,
            organizationName: selectedOrg?.name,
          });
        },
      });

      if (result.completed) {
        setOpen(false);
        navigate(HOME_ROUTE, { replace: true });
      }
    } catch (err) {
      const alreadySent =
        err?.response?.status === 429 ||
        String(err?.response?.data?.errors?.message || '').includes('OTP already sent');

      if (alreadySent) {
        setOpen(false);
        setMfaState({
          organizationId,
          organizationName: selectedOrg?.name,
        });

        return;
      }

      const message =
        err?.response?.data?.errors?.message ||
        err?.response?.data?.message ||
        'Unable to switch organization. Please try again.';

      setOrganizationsError(message);
      dispatch(
        showSnackbar({
          message,
          severity: 'error',
        }),
      );
    } finally {
      setSwitching(false);
    }
  };

  const handleMfaVerified = (token) => {
    applySelectedSession(dispatch, queryClient);
    setMfaState(null);
    navigate(HOME_ROUTE, { replace: true });
  };

  if (!org?.name) {
    return <Skeleton variant="rounded" width={240} height={40} sx={{ borderRadius: 1.5 }} />;
  }

  if (!hasMultipleOrganizations) {
    return null;
  }

  const trn = getOrgTrn(org);

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        sx={{
          justifyContent: 'flex-start',
          textAlign: 'left',
          textTransform: 'none',
          borderColor: 'divider',
          color: 'text.primary',
          px: 1.25,
          py: 0.5,
          minHeight: 40,
          minWidth: { xs: 200, sm: 240 },
          maxWidth: 320,
          borderRadius: 1.5,
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            ...orgIconBoxSx,
            width: 28,
            height: 28,
            mr: 1,
            borderRadius: '6px',
          }}
        >
          <ApartmentOutlined sx={{ fontSize: 16, color: 'action.active' }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.8125rem' }} noWrap>
            {org.name}
          </Typography>
          {trn ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontFamily: 'monospace', lineHeight: 1.2, fontSize: '0.6875rem' }}
              noWrap
            >
              TRN: {trn}
            </Typography>
          ) : null}
        </Box>
        <KeyboardArrowDown sx={{ fontSize: 18, color: 'action.active', ml: 0.25, flexShrink: 0 }} />
      </Button>

      <OrganizationSelectModal
        open={open}
        organizations={enrichedOrganizations}
        selectedOrganizationId={org?.id}
        loading={organizationsLoading}
        submitting={switching}
        error={organizationsError}
        onSelectOrganization={handleSelectOrganization}
        onClose={handleClose}
        emptyMessage="No organizations available."
      />

      <OrganizationMfaModal
        open={Boolean(mfaState?.organizationId)}
        email={user?.email || readAuthEmail()}
        organizationId={mfaState?.organizationId}
        organizationName={mfaState?.organizationName}
        submitting={mfaSubmitting}
        setSubmitting={setMfaSubmitting}
        onVerified={handleMfaVerified}
        onCancel={handleMfaCancel}
      />
    </>
  );
};

export default OrgSwitcher;
